import { Plus, Shield, Users } from 'lucide-react';

import { Role } from '@/api/queries/useGetRoles';
import { Button } from '@/components/atoms/new-button';

interface RolesSidebarProps {
  roles: Role[];
  selectedRole: Role | null;
  onSelectRole: (role: Role) => void;
  onCreateRole: () => void;
  isLoading: boolean;
  isCreating?: boolean;
}

export const RolesSidebar = ({
  roles,
  selectedRole,
  onSelectRole,
  onCreateRole,
  isLoading,
  isCreating = false,
}: RolesSidebarProps) => {
  const sortedRoles = [...roles].sort((a, b) => {
    // Administrator first, then alphabetical
    if (a.name === 'Administrator' && b.name !== 'Administrator') {
      return -1;
    }

    if (b.name === 'Administrator' && a.name !== 'Administrator') {
      return 1;
    }

    return a.name.localeCompare(b.name);
  });

  if (isLoading) {
    return (
      <div className="w-1/4 border-r border-gray-200 bg-white p-4">
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded bg-gray-200" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-1/4 border-r border-gray-200 bg-white p-4">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Roles</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={onCreateRole}
          className="text-xs"
          disabled={isLoading || isCreating}
        >
          <Plus className="h-3 w-3" />
          Add
        </Button>
      </div>

      <div className="space-y-1">
        {sortedRoles.map((role) => (
          <button
            key={role.id}
            onClick={() => onSelectRole(role)}
            className={`w-full rounded-md px-3 py-2 text-left transition-colors ${
              selectedRole?.id === role.id
                ? 'border border-blue-200 bg-blue-100 text-blue-700'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium">{role.name}</span>
                  {role.isDefault && (
                    <span className="flex-shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      Default
                    </span>
                  )}
                </div>

                <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                  <Users className="h-3 w-3" />
                  <span>
                    {role.userCount} {role.userCount === 1 ? 'user' : 'users'}
                  </span>
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
